import {monthLabel, parsePeriodKey} from '@vat/engine/month';
import type {LandlordTaxProfile} from '@vat/engine/types';

/**
 * Deutsche Formatierung für PDF, Excel-Titel, Mail und Aufgaben (reines TypeScript).
 * Intl 'de-DE' ist in Chrome und Node ≥ 13 (full-icu) verfügbar.
 *
 * @author Anthony Nahas
 * @since 11.09.26
 */
export const MINUS_SIGN = '−';

const de2 = new Intl.NumberFormat('de-DE', {minimumFractionDigits: 2, maximumFractionDigits: 2});
const de0 = new Intl.NumberFormat('de-DE', {minimumFractionDigits: 0, maximumFractionDigits: 0});

const pad2 = (n: number): string => String(n).padStart(2, '0');

const isFiniteNumber = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);

export const formatEuro = (value: number | null | undefined): string => {
    if (!isFiniteNumber(value)) {
        return '–';
    }
    return `${value < 0 ? MINUS_SIGN : ''}${de2.format(Math.abs(value))} €`;
};

/** Bemessungsgrundlage in vollen Euro (Vordruck): Abschneiden Richtung Null, negativ zulässig (R11). */
export const formatFullEuro = (value: number | null | undefined): string => {
    if (!isFiniteNumber(value)) {
        return '–';
    }
    const truncated = Math.trunc(value);
    return `${truncated < 0 ? MINUS_SIGN : ''}${de0.format(Math.abs(truncated))} €`;
};

export const formatPercent = (share: number): string => `${de2.format(share * 100)} %`;

export const formatDateDE = (d: Date | null | undefined): string =>
    d instanceof Date && !isNaN(d.getTime()) ? `${pad2(d.getDate())}.${pad2(d.getMonth() + 1)}.${d.getFullYear()}` : '–';

export const formatDateTimeDE = (d: Date | null | undefined): string =>
    d instanceof Date && !isNaN(d.getTime()) ? `${formatDateDE(d)}, ${pad2(d.getHours())}:${pad2(d.getMinutes())} Uhr` : '–';

/** 'März 2026' bzw. '2. Quartal 2026' (Plan 1: monthLabel/parsePeriodKey). */
export const periodLabel = (year: number, periodKey: string): string => {
    const parsed = parsePeriodKey(periodKey);
    if (!parsed) {
        throw new Error(`Ungültiger periodKey: ${periodKey}`);
    }
    return parsed.type === 'MONAT' ? monthLabel(`${year}-${pad2(parsed.index)}`) : `${parsed.index}. Quartal ${year}`;
};

export const steuernummerLabel = (profile: Pick<LandlordTaxProfile, 'steuernummer' | 'steuernummerElster'>): string => {
    if (!profile?.steuernummer) {
        return '–';
    }
    return profile.steuernummerElster ? `${profile.steuernummer} (ELSTER ${profile.steuernummerElster})` : profile.steuernummer;
};

export const kz83Label = (kz83: number): string =>
    kz83 < 0 ? 'Überschuss (Erstattung)' : 'Verbleibende Umsatzsteuer-Vorauszahlung (Zahllast)';
